import { useState, useRef, useCallback } from 'react';

/**
 * Hook to manage DOB confirmation when patient appears < 1 year old.
 * 
 * @param {string} dobValue - Current DOB string (YYYY-MM-DD)
 * @param {function} setDobValue - Setter for the DOB state
 * @returns {{ dobInputRef, dobConfirmed, showDobConfirmDialog, handleDobBlur, handleDobChange, confirmDob, fixDob }}
 */
export default function useDobConfirm(dobValue, setDobValue) {
  const [showDobConfirmDialog, setShowDobConfirmDialog] = useState(false);
  const [dobConfirmed, setDobConfirmed] = useState(false);
  const dobInputRef = useRef(null);

  const handleDobBlur = useCallback(() => {
    if (!dobValue || dobConfirmed) return;
    const parts = dobValue.split('-');
    if (parts.length !== 3) return;
    const dob = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    const today = new Date();
    const oneYearAgo = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
    if (dob > oneYearAgo) {
      setShowDobConfirmDialog(true);
    }
  }, [dobValue, dobConfirmed]);

  const handleDobChange = useCallback((newValue) => {
    setDobValue(newValue);
    setDobConfirmed(false);
  }, [setDobValue]);

  const confirmDob = useCallback(() => {
    setDobConfirmed(true);
    setShowDobConfirmDialog(false);
  }, []);

  const fixDob = useCallback(() => {
    setShowDobConfirmDialog(false);
    setDobValue('');
    setDobConfirmed(false);
    setTimeout(() => dobInputRef.current?.focus(), 100);
  }, [setDobValue]);

  return {
    dobInputRef,
    dobConfirmed,
    showDobConfirmDialog,
    handleDobBlur,
    handleDobChange,
    confirmDob,
    fixDob,
  };
}
